/**
 * Reference-model synchronous constants (G2-01, HG-2A).
 *
 * The single surviving in-code copy of the pilot competency enumerations.
 * Every other copy across api/, frontend/ and mobile/ imports from here.
 *
 * FLAGGED (per HG-2A G2-01.3): these are module-scope synchronous constants,
 * kept because z.enum schema construction and UI label maps need values at
 * module load, before any database round trip is possible. The runtime read
 * path is the loader (./loader.ts): presentation and validation surfaces
 * resolve the registry at run time (G2-02/G2-03); these constants exist for
 * the reference model only and must match the migration 233 seed's code set.
 *
 * Codes and order derive from FRAMEWORK_LISTING_ORDER_V1 (ICD 8.11); there
 * is deliberately no second hard-coded order here.
 *
 * Three display-name sets exist historically and are kept as three explicit
 * exports so each consuming surface stays byte-identical:
 *  - PROMPT names: what LLM prompts instruct with (PRO-first declaration
 *    order, load-bearing as the evidence-extraction iteration order).
 *  - SHORT names: abbreviated display labels (Part B/C/D views, exports).
 *  - FULL names: long-form labels (export types, version history, mobile).
 * G2-02 supersedes display surfaces with registry reads (wm_label / title);
 * prompt strings stay pinned until the G4 prompt parameterisation pass.
 */
import { FRAMEWORK_LISTING_ORDER_V1 } from '../icd/enums';

/** Pilot competency code for the reference model (OPR excluded: operator scope). */
export type PilotCompetencyCode = Exclude<
  (typeof FRAMEWORK_LISTING_ORDER_V1)[number],
  'OPR'
>;

/**
 * Every code the Framework Listing Order can carry, OPR included (ICD 8.11).
 *
 * This is the type an EXPORT PAYLOAD carries (HG-3B G3-04.3). PilotCompetencyCode
 * above is the reference model, and it excludes OPR on purpose: the reference
 * model holds no Operator Competency, so OB_CODES and the three display-name
 * maps are keyed by it and must stay keyed by it.
 *
 * The export payload is a different thing. It carries whatever the tenant's
 * effective framework version graded, and a tenant version that defines an
 * Operator Competency puts OPR last in its listing order. Pinning the payload to
 * the nine reference codes meant an operator order with OPR could not be
 * expressed at all, which is the gap HG-2A flagged and this type closes. It is
 * derived from FRAMEWORK_LISTING_ORDER_V1 rather than written out, so there is
 * still no second hard-coded list of competency codes in this file.
 */
export type FrameworkCompetencyCode = (typeof FRAMEWORK_LISTING_ORDER_V1)[number];

/**
 * The nine reference-model competency codes in Framework Listing Order
 * (KNO FPM PRO FPA COM WLM PSD LTW SAW), derived from the ICD constant.
 */
export const PILOT_COMPETENCY_CODES: readonly PilotCompetencyCode[] =
  FRAMEWORK_LISTING_ORDER_V1.filter(
    (code): code is PilotCompetencyCode => code !== 'OPR',
  );

/**
 * Canonical display order for Part C / Part D rows in PDF, DOCX and the
 * online viewer. Identical to PILOT_COMPETENCY_CODES; the alias preserves
 * the name the codebase has used since Phase 5d.
 */
export const COMPETENCY_ORDER: readonly PilotCompetencyCode[] = PILOT_COMPETENCY_CODES;

/**
 * Prompt-facing names in PRO-first declaration order.
 * LOAD-BEARING: object key order is the evidence-extraction iteration order
 * (unrelated to display order). Strings are pinned; changing them changes
 * LLM behaviour and belongs to the G4 prompt regression pass.
 */
export const PILOT_COMPETENCY_PROMPT_NAMES = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Management - Automation',
  FPM: 'Flight Path Management - Manual Control',
  KNO: 'Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Problem Solving & Decision Making',
  SAW: 'Situational Awareness',
  WLM: 'Workload Management',
} as const;

/** Abbreviated display labels (historical Part B/C/D and export surfaces). */
export const PILOT_COMPETENCY_SHORT_NAMES = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Mgmt - Auto',
  FPM: 'Flight Path Mgmt - Manual',
  KNO: 'Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Prob. Solving & Decision Making',
  SAW: 'Situation Awareness',
  WLM: 'Workload Management',
} as const;

/** Long-form display labels (export types, version history, mobile). */
export const PILOT_COMPETENCY_FULL_NAMES = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Management - Automation',
  FPM: 'Flight Path Management - Manual',
  KNO: 'Application of Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Problem Solving & Decision Making',
  SAW: 'Situational Awareness',
  WLM: 'Workload Management',
} as const;

/** One observable behaviour of the reference model as prompts cite it. */
export interface ReferenceObservableBehaviour {
  code: string;
  description: string;
}

/**
 * The 73 reference observable behaviours, moved verbatim from
 * api/lib/trainee-assessment/types.ts (WingMentor Core Framework 2.1-2.9).
 *
 * PROMPT WORDING ONLY: presentation surfaces read behaviour text and
 * icao_ref from the registry at run time (GCH-06; the registry corrects
 * FPM-3 to "aircraft attitude"). These strings feed LLM prompts and stay
 * pinned until the G4 prompt regression pass.
 */
export const OB_CODES: Record<PilotCompetencyCode, ReferenceObservableBehaviour[]> = {
  KNO: [
    { code: 'KNO-1', description: 'Demonstrates practical and applicable knowledge of limitations and systems and their interaction.' },
    { code: 'KNO-2', description: 'Demonstrates required knowledge of published operating instructions.' },
    { code: 'KNO-3', description: 'Demonstrates knowledge of the physical environment, including routings, weather, airports, and operational infrastructure.' },
    { code: 'KNO-4', description: 'Demonstrates appropriate knowledge of applicable legislation.' },
    { code: 'KNO-5', description: 'Knows where to source required information.' },
    { code: 'KNO-6', description: 'Demonstrates a positive interest in acquiring knowledge.' },
    { code: 'KNO-7', description: 'Applies knowledge effectively.' },
  ],
  PRO: [
    { code: 'PRO-1', description: 'Identifies where to find procedures and regulations.' },
    { code: 'PRO-2', description: 'Applies relevant operating instructions, procedures, and techniques in a timely manner.' },
    { code: 'PRO-3', description: 'Follows SOPs unless a higher degree of safety dictates an appropriate deviation.' },
    { code: 'PRO-4', description: 'Operates aircraft systems and associated equipment correctly.' },
    { code: 'PRO-5', description: 'Monitors aircraft systems status.' },
    { code: 'PRO-6', description: 'Complies with applicable regulations.' },
    { code: 'PRO-7', description: 'Applies relevant procedural knowledge.' },
  ],
  COM: [
    { code: 'COM-1', description: 'Determines that the recipient is ready and able to receive information.' },
    { code: 'COM-2', description: 'Selects appropriately what, when, how, and with whom to communicate.' },
    { code: 'COM-3', description: 'Conveys messages clearly, accurately, and concisely.' },
    { code: 'COM-4', description: 'Confirms that the recipient demonstrates understanding of important information.' },
    { code: 'COM-5', description: 'Listens actively and demonstrates understanding when receiving information.' },
    { code: 'COM-6', description: 'Asks relevant and effective questions.' },
    { code: 'COM-7', description: 'Uses appropriate escalation in communication to resolve identified deviations.' },
    { code: 'COM-8', description: 'Uses and interprets non-verbal communication appropriately.' },
    { code: 'COM-9', description: 'Adheres to standard radiotelephony phraseology and procedures.' },
    { code: 'COM-10', description: 'Accurately reads, interprets, constructs, and responds to datalink messages in English.' },
  ],
  FPA: [
    { code: 'FPA-1', description: 'Uses appropriate flight management and guidance systems and automation as installed.' },
    { code: 'FPA-2', description: 'Monitors and detects deviations from the intended flight path and takes appropriate action.' },
    { code: 'FPA-3', description: 'Manages the flight path safely to achieve optimum operational performance.' },
    { code: 'FPA-4', description: 'Maintains the intended flight path using automation while managing other tasks.' },
    { code: 'FPA-5', description: 'Selects the appropriate level and mode of automation considering phase of flight and workload.' },
    { code: 'FPA-6', description: 'Effectively monitors automation, including engagement and mode transitions.' },
  ],
  FPM: [
    { code: 'FPM-1', description: 'Controls the airplane manually with accuracy and smoothness.' },
    { code: 'FPM-2', description: 'Monitors and detects deviations from the intended flight path and takes appropriate action.' },
    { code: 'FPM-3', description: 'Manually controls the airplane using relationships between altitude, speed, thrust, and navigation cues.' },
    { code: 'FPM-4', description: 'Manages the flight path safely to achieve optimum operational performance.' },
    { code: 'FPM-5', description: 'Maintains the intended flight path while managing other tasks and distractions.' },
    { code: 'FPM-6', description: 'Uses appropriate flight management and guidance systems as applicable.' },
    { code: 'FPM-7', description: 'Effectively monitors flight guidance systems and transitions.' },
  ],
  LTW: [
    { code: 'LTW-1', description: 'Encourages team participation and open communication. Includes soliciting agreement, inviting input, and confirming alignment from the other pilot.' },
    { code: 'LTW-2', description: 'Demonstrates initiative and provides direction when required.' },
    { code: 'LTW-3', description: 'Engages others in planning. Includes proposing options, suggesting adjustments, or coordinating task ownership during planning conversations.' },
    { code: 'LTW-4', description: "Considers inputs from others. Includes acknowledging the other pilot's proposals and explicitly accepting or modifying them." },
    { code: 'LTW-5', description: 'Gives and receives feedback constructively.' },
    { code: 'LTW-6', description: 'Addresses and resolves conflicts constructively.' },
    { code: 'LTW-7', description: 'Exercises decisive leadership when required.' },
    { code: 'LTW-8', description: 'Accepts responsibility for decisions and actions.' },
    { code: 'LTW-9', description: 'Carries out instructions when directed.' },
    { code: 'LTW-10', description: 'Applies effective intervention strategies to resolve deviations.' },
    { code: 'LTW-11', description: 'Manages cultural and language challenges as applicable.' },
  ],
  PSD: [
    { code: 'PSD-1', description: 'Identifies, assesses, and manages threats and errors in a timely manner.' },
    { code: 'PSD-2', description: 'Seeks accurate and adequate information from appropriate sources.' },
    { code: 'PSD-3', description: 'Identifies and verifies what and why things have gone wrong, if appropriate.' },
    { code: 'PSD-4', description: 'Perseveres in working through problems while prioritising safety.' },
    { code: 'PSD-5', description: 'Identifies and considers appropriate options.' },
    { code: 'PSD-6', description: 'Applies appropriate and timely decision-making techniques.' },
    { code: 'PSD-7', description: 'Monitors, reviews, and adapts decisions as required.' },
    { code: 'PSD-8', description: 'Adapts when faced with situations where no guidance or procedure exists.' },
    { code: 'PSD-9', description: 'Demonstrates resilience when encountering unexpected events.' },
  ],
  SAW: [
    { code: 'SAW-1', description: 'Monitors and assesses the state of the airplane and its systems.' },
    { code: 'SAW-2', description: "Monitors and assesses the airplane's energy state and anticipated flight path." },
    { code: 'SAW-3', description: 'Monitors and assesses the environment as it may affect the operation.' },
    { code: 'SAW-4', description: 'Validates the accuracy of information and checks for gross errors.' },
    { code: 'SAW-5', description: 'Maintains awareness of people involved in or affected by the operation and their capacity to perform.' },
    { code: 'SAW-6', description: 'Develops effective contingency plans based upon potential risks.' },
    { code: 'SAW-7', description: 'Responds to indications of reduced situation awareness.' },
  ],
  WLM: [
    { code: 'WLM-1', description: 'Exercises self-control in all situations.' },
    { code: 'WLM-2', description: 'Plans, prioritises, and schedules tasks effectively.' },
    { code: 'WLM-3', description: 'Manages time efficiently.' },
    { code: 'WLM-4', description: 'Offers and gives assistance.' },
    { code: 'WLM-5', description: 'Delegates tasks appropriately.' },
    { code: 'WLM-6', description: 'Seeks and accepts assistance when appropriate.' },
    { code: 'WLM-7', description: 'Monitors, reviews, and cross-checks actions conscientiously.' },
    { code: 'WLM-8', description: 'Verifies that tasks are completed to the expected outcome.' },
    { code: 'WLM-9', description: 'Manages and recovers from interruptions, distractions, variations, and failures effectively.' },
  ],
};
